import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Check, X, AlertTriangle, Undo2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, ZoomImage, CopyButton, Countdown, usePoll, fmtUSDT, fmtINR } from "@/components/common";
import Uploader from "@/components/Uploader";

const STATUS_OPTIONS = ["Under Review", "Payment Processing", "Pending Settlement", "Rejected", "Cancelled", "Completed"];
const SEG_LABEL = { payment_sent: "Awaiting confirmation", customer_confirmed: "Confirmed", dispute: "Disputed", expired: "Expired — review" };
const CANCELABLE_STATUSES = ["Pending", "Under Review", "Payment Processing", "Partially Paid", "Pending Settlement",
  "Payment Sent", "Confirmation Pending", "Payment Dispute / Needs Review", "Confirmation Expired", "Cancellation Requested"];

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [payout, setPayout] = useState({ amount: "", method: "UPI", reference: "", notes: "", file_id: null });
  const [busy, setBusy] = useState(false);
  const [decisionReason, setDecisionReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const load = () => api.get(`/admin/orders/${orderId}`).then((r) => setData(r.data)).catch(() => {});
  usePoll(load, 5000, [orderId]);
  if (!data) return <div className="text-slate-400">Loading…</div>;
  const o = data.order;
  const snap = o.payout_snapshot || {};

  const recordPayout = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post(`/admin/orders/${orderId}/payouts`, payout);
      toast.success("Payout recorded — 10-minute timer started");
      setPayout({ amount: "", method: "UPI", reference: "", notes: "", file_id: null }); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    try { await api.post(`/admin/orders/${orderId}/status`, { status }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const decide = async (action) => {
    if (!decisionReason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/orders/${orderId}/cancel-decision`, { action, reason: decisionReason });
      toast.success(res.message); setDecisionReason(""); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const openCancelReturn = async () => {
    setCancelOpen(true); setCancelPreview(null); setCancelReason("");
    try { const { data } = await api.get(`/admin/orders/${orderId}/cancel-preview`); setCancelPreview(data); }
    catch (e) { toast.error(errMsg(e)); setCancelOpen(false); }
  };

  const confirmCancelReturn = async () => {
    if (!cancelReason.trim()) return toast.error("Reason required");
    setBusy(true);
    try {
      const { data: res } = await api.post(`/admin/orders/${orderId}/cancel-return`, { reason: cancelReason });
      toast.success(`${res.message} — ${res.status}`);
      setCancelOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <button onClick={() => nav("/admin/orders")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-lg font-bold text-slate-900">{o.order_id}</div>
            <div className="text-sm text-slate-500 mt-1">{o.customer_name} · {o.product_name}</div>
          </div>
          <StatusBadge status={o.status} testid="admin-order-status" />
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm">
          <Info label="USDT (held)" value={`${fmtUSDT(o.amount)}`} />
          <Info label="Locked rate" value={`${fmtINR(o.rate)} (${o.rate_source})`} />
          <Info label="Expected INR" value={fmtINR(o.expected_inr)} />
          <Info label="Paid INR" value={fmtINR(o.paid_inr)} tone="green" />
          <Info label="Remaining INR" value={fmtINR(o.remaining_inr)} />
          <Info label="Settled USDT" value={`${fmtUSDT(o.settled_usdt)}`} tone="green" />
          <Info label="Remaining held USDT" value={`${fmtUSDT(o.remaining_held_usdt)}`} />
          <Info label="Excess deducted" value={fmtINR(o.excess_inr_deducted)} />
        </div>

        <div className="mt-5 border-t border-slate-200 pt-4">
          <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Customer payout details (snapshot at order time)</div>
          {snap.upi_id ? (
            <div className="flex flex-wrap items-center gap-4" data-testid="admin-upi-details">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-900">{snap.upi_id}</span>
                <CopyButton value={snap.upi_id} testid="copy-upi-button" label="Copy UPI" />
              </div>
              <div className="text-sm text-slate-600">Name: <span className="font-medium text-slate-900">{snap.upi_name || "—"}</span></div>
              <div className="text-xs text-slate-400">Customer ID: {o.customer_id}</div>
              {snap.upi_qr_file_id && (
                <ZoomImage fileId={snap.upi_qr_file_id} className="h-32 w-32 object-contain rounded-lg border border-slate-200 bg-white" testid="admin-upi-qr" placeholder="" />
              )}
            </div>
          ) : o.bank ? (
            <div className="text-sm text-slate-700" data-testid="admin-bank-details">
              {o.bank.holder} · {o.bank.bank_name} · A/C {o.bank.account_number} · IFSC {o.bank.ifsc}
            </div>
          ) : <div className="text-sm text-slate-400">No payout details.</div>}
          {o.bank_proof_file_id && (
            <div className="mt-3" data-testid="admin-bank-proof-block">
              <div className="text-xs text-slate-500 mb-1">Bank account verification screenshot (pending review)</div>
              <ZoomImage fileId={o.bank_proof_file_id} className="max-h-48 w-auto rounded-lg border border-slate-200 bg-slate-50" testid="admin-bank-proof" placeholder="" />
            </div>
          )}
        </div>

        {o.status === "Cancellation Requested" && (
          <div className="mt-5 border border-amber-300 bg-amber-50 rounded-xl p-4 space-y-3" data-testid="cancel-decision-panel">
            <div className="text-sm text-amber-900">
              <strong>Cancellation requested</strong> ({o.cancel_request?.request_type}) · {o.cancel_request?.reason}
            </div>
            <div className="text-xs text-amber-800">Reconcile all payouts before approving. Only eligible unsold USDT is released; sent/disputed amounts stay held.</div>
            <input value={decisionReason} onChange={(e) => setDecisionReason(e.target.value)} placeholder="Decision reason (required)"
              data-testid="cancel-decision-reason" className="w-full px-3 py-2 rounded-xl border border-amber-300 outline-none text-sm" />
            <div className="flex gap-2">
              <button onClick={() => decide("approve")} disabled={busy} data-testid="approve-cancel-button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60"><Check className="w-4 h-4" /> Approve & release eligible</button>
              <button onClick={() => decide("reject")} disabled={busy} data-testid="reject-cancel-button"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 disabled:opacity-60"><X className="w-4 h-4" /> Reject</button>
            </div>
          </div>
        )}

        <div className="mt-5 flex gap-2 flex-wrap">
          {STATUS_OPTIONS.map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`set-status-${s.replace(/[^a-z]/gi, "").toLowerCase()}`}
              className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50">{s}</button>
          ))}
        </div>

        {CANCELABLE_STATUSES.includes(o.status) && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <button onClick={openCancelReturn} data-testid="admin-cancel-return-button"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700">
              <Undo2 className="w-4 h-4" /> Cancel & Return Remaining USDT
            </button>
            <p className="text-xs text-slate-400 mt-1.5">Settlements for verified payments are kept; only unsettled USDT is returned. Sent/disputed amounts stay held.</p>
          </div>
        )}
      </div>

      {!["Completed", "Cancelled", "Rejected", "Partially Settled — Cancelled", "Cancelling"].includes(o.status) && (
        <form onSubmit={recordPayout} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h2 className="font-heading font-semibold text-slate-900">Record INR payout (starts 10-min timer)</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-medium text-slate-700">INR amount</span>
              <input type="number" step="0.01" required value={payout.amount} onChange={(e) => setPayout({ ...payout, amount: e.target.value })}
                data-testid="payout-amount-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 font-mono outline-none" /></label>
            <label className="block"><span className="text-sm font-medium text-slate-700">Method</span>
              <select value={payout.method} onChange={(e) => setPayout({ ...payout, method: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 bg-white outline-none">
                <option>UPI</option><option>Bank Transfer</option><option>IMPS</option><option>NEFT</option></select></label>
            <label className="block"><span className="text-sm font-medium text-slate-700">UTR / reference</span>
              <input required value={payout.reference} onChange={(e) => setPayout({ ...payout, reference: e.target.value })}
                data-testid="payout-reference-input" className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 font-mono outline-none" /></label>
            <label className="block"><span className="text-sm font-medium text-slate-700">Notes</span>
              <input value={payout.notes} onChange={(e) => setPayout({ ...payout, notes: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none" /></label>
          </div>
          <div><span className="text-sm font-medium text-slate-700">Payment proof (optional)</span>
            <div className="mt-1"><Uploader fileId={payout.file_id} onUploaded={(id) => setPayout({ ...payout, file_id: id })} testid="payout-proof-upload" label="Attach payment proof" /></div></div>
          <button disabled={busy} data-testid="record-payout-button" className="px-5 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60 inline-flex items-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Mark Payment Sent
          </button>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <h2 className="font-heading font-semibold text-slate-900 mb-3">Payout segments & timers</h2>
        {data.segments.length === 0 ? <div className="text-sm text-slate-400 py-6 text-center">No payouts recorded.</div> : (
          <div className="space-y-3" data-testid="admin-segments-list">
            {data.segments.map((s) => (
              <div key={s.segment_id} className="border border-slate-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="font-mono text-sm text-slate-900">{s.segment_id}</div>
                    <div className="text-sm text-slate-600">{fmtINR(s.amount)} · {s.method} · UTR {s.reference}</div>
                    <div className="text-xs text-slate-400">by {s.actor} · {s.payment_sent_at ? new Date(s.payment_sent_at).toLocaleString() : ""}</div>
                    {s.dispute_reason && <div className="text-xs text-rose-600 mt-1">Dispute: {s.dispute_reason}</div>}
                  </div>
                  <div className="flex items-center gap-3">
                    {s.status === "payment_sent" && s.expiry_at && <Countdown expiry={s.expiry_at} />}
                    <StatusBadge status={s.status} />
                  </div>
                </div>
                {s.file_id && <ZoomImage fileId={s.file_id} className="max-h-48 rounded-lg border border-slate-200 bg-slate-50" testid={`admin-segment-proof-${s.segment_id}`} placeholder="" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={() => !busy && setCancelOpen(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cancel-return-modal">
            <div className="flex items-start justify-between">
              <h3 className="font-heading font-bold text-lg text-slate-900">Cancel & return remaining USDT</h3>
              <button onClick={() => setCancelOpen(false)} disabled={busy} data-testid="cancel-return-close" className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-4 h-4" /></button>
            </div>
            {!cancelPreview ? <div className="text-sm text-slate-400 py-6 text-center">Calculating…</div> : (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm" data-testid="cancel-return-summary">
                  <Info label="Original USDT" value={fmtUSDT(cancelPreview.amount_usdt)} />
                  <Info label="Locked rate" value={fmtINR(cancelPreview.locked_rate)} />
                  <Info label="Verified INR paid" value={fmtINR(cancelPreview.verified_paid_inr)} />
                  <Info label="USDT settled (kept)" value={fmtUSDT(cancelPreview.settled_usdt)} />
                  <Info label="USDT to return" value={fmtUSDT(cancelPreview.return_usdt)} tone="green" />
                  <Info label="Resulting status" value={cancelPreview.resulting_status} />
                </div>
                {(cancelPreview.warnings || []).map((w, i) => (
                  <div key={i} className="flex gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="cancel-return-warning">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{w}
                  </div>
                ))}
                <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason (required)"
                  data-testid="cancel-return-reason" className="w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
                <button onClick={confirmCancelReturn} disabled={busy} data-testid="cancel-return-confirm-button"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-60">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirm cancellation & return {cancelPreview.return_usdt} USDT
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value, tone }) {
  return <div><div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-mono font-semibold mt-0.5 ${tone === "green" ? "text-emerald-700" : "text-slate-900"}`}>{value}</div></div>;
}
